import { apiClient } from './api';
import type { AnalyticsEventData, AnalyticsResponse } from './api';

interface QueuedEvent {
  id: string;
  data: AnalyticsEventData;
  timestamp: number;
  retryCount: number;
}

interface AnalyticsConfig {
  maxRetries: number;
  retryDelay: number;
  queueMaxSize: number;
  flushInterval: number;
  enableDebug: boolean;
}

class AnalyticsService {
  private eventQueue: QueuedEvent[] = [];
  private isOnline: boolean = navigator.onLine;
  private flushTimer: number | null = null;
  private config: AnalyticsConfig = {
    maxRetries: 3,
    retryDelay: 1000, // 1 second
    queueMaxSize: 100,
    flushInterval: 5000, // 5 seconds
    enableDebug: import.meta.env.DEV || false,
  };

  constructor(config?: Partial<AnalyticsConfig>) {
    this.config = { ...this.config, ...config };
    this.setupEventListeners();
    this.startFlushTimer();
    this.loadQueueFromStorage();
  }

  private setupEventListeners() {
    // Monitor network status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.debug('Network came online, flushing queue');
      this.flushQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.debug('Network went offline');
    });

    // Save queue before page unload
    window.addEventListener('beforeunload', () => {
      this.saveQueueToStorage();
      // Try to flush any remaining events
      if (this.eventQueue.length > 0) {
        this.flushQueue();
      }
    });

    // Handle visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.flushQueue();
      } else {
        this.saveQueueToStorage();
      }
    });
  }

  private startFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = window.setInterval(() => {
      if (this.eventQueue.length > 0 && this.isOnline) {
        this.flushQueue();
      }
    }, this.config.flushInterval);
  }

  private generateEventId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private debug(message: string, data?: any) {
    if (this.config.enableDebug) {
      console.log(`[Analytics] ${message}`, data || '');
    }
  }

  private saveQueueToStorage() {
    try {
      if (this.eventQueue.length > 0) {
        localStorage.setItem('analytics_queue', JSON.stringify(this.eventQueue));
        this.debug(`Saved ${this.eventQueue.length} events to storage`);
      }
    } catch (error) {
      this.debug('Failed to save queue to storage', error);
    }
  }

  private loadQueueFromStorage() {
    try {
      const stored = localStorage.getItem('analytics_queue');
      if (stored) {
        const parsedQueue = JSON.parse(stored);
        this.eventQueue = Array.isArray(parsedQueue) ? parsedQueue : [];
        localStorage.removeItem('analytics_queue');
        this.debug(`Loaded ${this.eventQueue.length} events from storage`);
      }
    } catch (error) {
      this.debug('Failed to load queue from storage', error);
      localStorage.removeItem('analytics_queue');
    }
  }

  private async sendEvent(queuedEvent: QueuedEvent): Promise<boolean> {
    try {
      // Add additional validation before sending
      const eventData = queuedEvent.data;
      if (!eventData.event_type) {
        this.debug('Skipping event send: missing event_type', { eventId: queuedEvent.id });
        return false;
      }

      if (!eventData.event_data?.user_id) {
        this.debug('Skipping event send: missing user_id', { eventId: queuedEvent.id });
        return false;
      }

      const response = await apiClient.trackEvent(eventData);
      this.debug('Event sent successfully', { eventId: queuedEvent.id, response });
      return true;
    } catch (error: any) {
      // Log specific details about 400 errors to help with debugging
      if (error?.message?.includes('400') || error?.message?.includes('Bad request')) {
        this.debug('Bad request error - event data may be invalid', {
          eventId: queuedEvent.id,
          eventData: queuedEvent.data,
          error: error.message
        });
      } else {
        this.debug('Failed to send event', { eventId: queuedEvent.id, error: error.message });
      }
      return false;
    }
  }

  private async flushQueue() {
    if (this.eventQueue.length === 0 || !this.isOnline) {
      return;
    }

    const eventsToProcess = [...this.eventQueue];
    const failedEvents: QueuedEvent[] = [];

    this.debug(`Flushing ${eventsToProcess.length} events`);

    for (const event of eventsToProcess) {
      const success = await this.sendEvent(event);

      if (!success) {
        event.retryCount++;
        if (event.retryCount <= this.config.maxRetries) {
          // Add delay for retry
          setTimeout(() => {
            failedEvents.push(event);
          }, this.config.retryDelay * event.retryCount);
        } else {
          this.debug('Event exceeded max retries, dropping', { eventId: event.id });
        }
      }
    }

    // Update queue with failed events that still have retries left
    this.eventQueue = failedEvents;

    if (this.eventQueue.length > 0) {
      this.debug(`${this.eventQueue.length} events remaining in queue for retry`);
    }
  }

  private addToQueue(eventData: AnalyticsEventData) {
    // Validate required fields before queuing
    if (!eventData.event_type) {
      this.debug('Skipping event with missing event_type', eventData);
      return;
    }

    // Ensure user_id is present in event_data
    if (!eventData.event_data?.user_id) {
      this.debug('Skipping event with missing user_id', eventData);
      return;
    }

    // Ensure queue doesn't exceed max size
    if (this.eventQueue.length >= this.config.queueMaxSize) {
      this.eventQueue.shift(); // Remove oldest event
      this.debug('Queue full, removed oldest event');
    }

    const queuedEvent: QueuedEvent = {
      id: this.generateEventId(),
      data: {
        ...eventData,
        metadata: {
          timestamp: new Date().toISOString(),
          source: 'frontend',
          version: '1.0.0',
          environment: import.meta.env.MODE || 'development',
          ...eventData.metadata,
        },
      },
      timestamp: Date.now(),
      retryCount: 0,
    };

    this.eventQueue.push(queuedEvent);
    this.debug('Added event to queue', { eventId: queuedEvent.id, queueSize: this.eventQueue.length });

    // Try to flush immediately if online
    if (this.isOnline) {
      // Use setTimeout to avoid blocking the main thread
      setTimeout(() => this.flushQueue(), 0);
    }
  }

  // Public methods
  trackPageView(path: string, title?: string, additionalData?: Record<string, any>) {
    this.addToQueue({
      event_type: 'page_view',
      event_data: {
        page_path: path,
        page_title: title || document.title,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        ...additionalData,
      },
    });
  }

  trackUserAction(action: string, additionalData?: Record<string, any>) {
    this.addToQueue({
      event_type: 'user_action',
      event_data: {
        action,
        page_path: window.location.pathname,
        ...additionalData,
      },
    });
  }

  trackButtonClick(buttonId?: string, buttonText?: string, additionalData?: Record<string, any>) {
    this.addToQueue({
      event_type: 'button_click',
      event_data: {
        button_id: buttonId,
        button_text: buttonText,
        page_path: window.location.pathname,
        ...additionalData,
      },
    });
  }

  trackFormSubmission(formId?: string, formType?: string, additionalData?: Record<string, any>) {
    this.addToQueue({
      event_type: 'form_submission',
      event_data: {
        form_id: formId,
        form_type: formType,
        page_path: window.location.pathname,
        ...additionalData,
      },
    });
  }

  trackError(errorType: string, errorMessage: string, additionalData?: Record<string, any>) {
    this.addToQueue({
      event_type: 'error',
      event_data: {
        error_type: errorType,
        error_message: errorMessage,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        ...additionalData,
      },
    });
  }

  trackAuthentication(authAction: 'login' | 'logout' | 'signup' | 'password_reset', authMethod?: 'email' | 'google', additionalData?: Record<string, any>) {
    this.addToQueue({
      event_type: 'authentication',
      event_data: {
        auth_action: authAction,
        auth_method: authMethod,
        page_path: window.location.pathname,
        ...additionalData,
      },
    });
  }

  // Custom event tracking
  trackCustomEvent(eventData: AnalyticsEventData) {
    this.addToQueue(eventData);
  }

  // Utility methods
  getQueueSize(): number {
    return this.eventQueue.length;
  }

  isQueueEmpty(): boolean {
    return this.eventQueue.length === 0;
  }

  clearQueue(): void {
    this.eventQueue = [];
    localStorage.removeItem('analytics_queue');
    this.debug('Queue cleared');
  }

  // Cleanup method
  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    // Final flush attempt
    this.saveQueueToStorage();

    // Remove event listeners
    window.removeEventListener('online', this.setupEventListeners);
    window.removeEventListener('offline', this.setupEventListeners);
    window.removeEventListener('beforeunload', this.setupEventListeners);
    document.removeEventListener('visibilitychange', this.setupEventListeners);
  }
}

// Create and export singleton instance
export const analyticsService = new AnalyticsService();

// Export the class for custom instances if needed
export { AnalyticsService };